import React, { useEffect, useState } from 'react';
import { Button, TextArea, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as Yup from 'yup';
import Icon from 'shared/components/Icon';
import { ErrorField } from 'shared/styles';
import { useAccessControl } from 'utils/access-control';
import { NoteType } from '../../../../types';
import { useResponseNoteMutation } from '../../../../hooks';

type ResponseNoteFormType = {
  notes?: string | null;
};

type ResponseNoteType = {
  notes?: string | null;
  showContactSection?: boolean;
  responseId: string;
  surveyId: string;
  noteId?: NoteType;
  inlineMode?: boolean;
  setEditNoteId?: (noteId: string | null) => void;
  disabled?: boolean;
  useNoteToggleButton?: boolean;
};

const AddResponseNote = (props: ResponseNoteType) => {
  const { t } = useTranslation();
  const { access } = useAccessControl();
  const [, setToast] = useToasts();
  const [isAddNoteOpen, toggleAddNote] = useState<boolean>(false);
  const {
    notes = '',
    showContactSection = false,
    responseId,
    surveyId,
    noteId,
    inlineMode = false,
    setEditNoteId,
    disabled = false,
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    register,
    formState: { submitCount, errors, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<ResponseNoteFormType>({
    resolver: yupResolver(
      Yup.object({
        notes: Yup.string()
          .trim()
          .min(
            1,
            `${t('forms.err_msg.note_min_char', {
              len: '1',
            })}`,
          )
          .max(
            1500,
            `${t('forms.err_msg.note_max_char', {
              len: '1500',
            })}`,
          ),
      }),
    ),
    mode: 'onBlur',
    defaultValues: {
      notes: notes,
    },
  });

  const { mutate: postNote, isLoading } = useResponseNoteMutation({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setValue('notes', '');
        toggleAddNote(false);
        if (setEditNoteId && noteId?.display_id) setEditNoteId(null);
      }
    },
  });

  const onSubmit = (val: ResponseNoteFormType) => {
    postNote(
      noteId
        ? {
            payload: {
              ...noteId,
              notes: val.notes,
            },
            responseId,
            surveyId,
            noteId: noteId.display_id,
          }
        : {
            payload: val,
            responseId,
            surveyId,
          },
    );
  };

  const handleCancel = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    reset();
    if (noteId && setEditNoteId) setEditNoteId(null);
  };

  useEffect(() => {
    if (noteId && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current)
          inputRef.current.scrollIntoView({
            behavior: 'smooth',
          });
      }, 100);
    }
  }, [noteId]);

  return (
    <>
      {access?.includes('response-edit:write') && (
        <AddNote
          isExpanded={showContactSection}
          inlineMode={inlineMode}
          disabled={disabled}
        >
          {!isAddNoteOpen && props.useNoteToggleButton ? (
            <OpenAddNoteContainer>
              <div
                className="op-nt-ctn-btn"
                role="button"
                tabIndex={0}
                onClick={() => {
                  toggleAddNote(true);
                }}
              >
                <div className="op-nt-ctn-icon">
                  <Icon
                    viewBox="0 0 14 14"
                    fill="none"
                    width="14"
                    height="14"
                    glyph="note"
                  />
                </div>
                <div className="op-nt-ctn-text">
                  {t('responses.response_note')}
                </div>
              </div>
            </OpenAddNoteContainer>
          ) : (
            <form className="note-container" onSubmit={handleSubmit(onSubmit)}>
              <div ref={inputRef}>
                <TextArea
                  {...register('notes')}
                  placeholder={`${t('forms.placeholders.type_note')}`}
                  rows={2}
                  value={watch('notes') || undefined}
                  maxLength={1500}
                  isErrored={submitCount > 0 && Boolean(errors.notes?.message)}
                  disabled={isLoading || disabled}
                  autoFocus
                />
              </div>
              <div className="action">
                <ErrorField className="message">
                  {submitCount > 0 && errors.notes?.message
                    ? errors.notes.message
                    : ''}
                </ErrorField>
                <div>
                  {noteId ? (
                    <Button
                      type="secondary"
                      inline
                      size="mini"
                      onClick={handleCancel}
                    >
                      {t('cancel')}
                    </Button>
                  ) : null}
                  <Button
                    type="primary"
                    size={noteId ? 'mini' : 'normal'}
                    inline
                    disabled={!isDirty || isLoading || disabled}
                  >
                    {noteId ? `${t('update')}` : `${t('add_note')}`}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </AddNote>
      )}
    </>
  );
};

export default AddResponseNote;

type AddNoteStyleType = {
  inlineMode: boolean;
  isExpanded: boolean;
  disabled: boolean;
};

const AddNote = styled.div<AddNoteStyleType>`
  background: #fff;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  ${({ inlineMode, isExpanded }: AddNoteStyleType) =>
    inlineMode
      ? `
      padding-top: 10px;
      position: relative;
      width: 100%;
      max-width: 575px;
      padding-bottom:18px;`
      : `
      padding: 18px;
      min-width: ${isExpanded ? '100%' : 'calc(100% - 300px)'};
      `}

  .note-container {
    background: ${({ disabled }) =>
      disabled ? 'rgba(239, 239, 239, 0.3)' : '#fff'};
    > div:first-child {
      border-radius: 0;
      div.input-basic {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      }
      div.input-container:focus-within {
        border-color: transparent;
        box-shadow: 0 0 0 2px #2c5cc5;
        border-radius: 4px;
      }

      textarea {
        height: 90px;
        padding: 10px 12px;
      }

      .input-basic textarea:focus ~ .end-max-length {
        opacity: 1;
      }

      .input-basic:focus-within {
        box-shadow: none !important;
        border: 1px solid #cfd7df !important;
      }
    }

    .action {
      padding: 8px 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #fff;
      border: 1px solid var(--border-color);
      border-top: 0;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;

      .message {
        margin: 0;
      }
    }
  }
`;

const OpenAddNoteContainer = styled.div`
  border: 1px solid var(--color-smoke-50);
  padding: 4px;
  border-radius: 6px;
  margin: 0 8px;
  div.op-nt-ctn-btn {
    display: flex;
    align-items: center;
    cursor: pointer;
    width: 100%;
    border-radius: 6px;
    padding: 8px;
    :focus {
      box-shadow: 0 0 0 2px #2c5cc5;
    }
  }
  div.op-nt-ctn-icon {
    background-color: #f3f6f8;
    margin-right: 12px;
    width: 32px;
    height: 32px;
    display: grid;
    place-content: center;
    border-radius: 50%;
  }
  div.op-nt-ctn-text {
    color: #12344d;
    font-size: 12px;
    font-weight: 500;
    line-height: 24px;
  }
`;
